// Hooks do FORMULÁRIO PÓS-LIGAÇÃO: a lista da gestão, o formulário em edição, o
// formulário ATIVO que o vendedor preenche e o envio das respostas. Mesmo padrão
// de use-dispositions.ts:
//   - `src/api/call-form.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// As chaves abaixo são as que já estavam espalhadas por cinco telas (lista,
// construtor, wrap-up do click-to-call, diálogo de desfecho e o botão do detalhe
// da chamada), string por string — centralizar não é renomear.
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  activateCallForm,
  createCallForm,
  deactivateCallForm,
  deleteCallForm,
  duplicateCallForm,
  getActiveCallForm,
  getCallForm,
  listCallForms,
  submitCallForm,
  updateCallForm,
  type CallFormDef,
  type FormField,
} from "@/api/call-form";

// As chaves DESTE domínio. Ressalvas herdadas que valem leitura:
//
//  - `active` (["call-form"]) é PREFIXO de `detail` (["call-form", id]), e isso
//    é o mecanismo, não um acidente: as duas telas de gestão invalidam
//    ["call-form"] e derrubam o ATIVO e todos os detalhes de uma vez. Faz
//    sentido — publicar/duplicar/excluir muda qual formulário está ativo.
//  - `list` (["call-forms"], plural) fica FORA desse prefixo, apesar do nome
//    quase igual. É por isso que a gestão invalida SEMPRE as duas chaves em
//    seguida; um invalidateQueries só em ["call-form"] deixaria a tabela velha.
//    Divergência pré-existente; preservada — e `useInvalidateCallForms` abaixo
//    é justamente o par das duas, para nenhuma tela esquecer uma delas.
//  - `detail` recebe `string | null`: o construtor monta a chave com o id que
//    tem (null quando é um formulário novo) e desliga a query por `enabled`. A
//    chave com `null` existe no cache do jeito que sempre existiu.
//  - As respostas por chamada (POST /v1/calls/:id/form) não têm chave nenhuma:
//    são escrita pura, e nada no front lê a resposta gravada.
export const callFormKeys = {
  active: ["call-form"] as const,
  detail: (id: string | null) => ["call-form", id] as const,
  list: ["call-forms"] as const,
};

// --- Leitura ---

// Formulário ATIVO da org. Três telas leem esta MESMA entrada de cache com gates
// diferentes, de propósito: o botão do detalhe da chamada sem gate nenhum, o
// diálogo de desfecho só quando abre (`enabled: open`) e a tela de click-to-call
// só em modo fila (`enabled: !!campaignLeadId`) — quem abriu uma reaproveita na
// outra, sem request a mais.
export function useActiveCallForm(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: callFormKeys.active,
    queryFn: getActiveCallForm,
    enabled: options.enabled,
  });
}

// Um formulário para edição. `enabled` é o que distingue "editar" de "criar":
// no construtor de um formulário novo não há id para buscar.
export function useCallForm(id: string | null, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: callFormKeys.detail(id),
    queryFn: () => getCallForm(id as string),
    enabled: options.enabled,
  });
}

export function useCallForms() {
  return useQuery({ queryKey: callFormKeys.list, queryFn: listCallForms });
}

// --- Escrita ---

// A invalidação é a MESMA para todas as mutações da gestão — as DUAS chaves,
// ["call-forms"] e ["call-form"] — então é do hook. `useCallback([queryClient])`
// e não uma seta solta: a identidade estável é o que permite passá-la adiante
// (ou pô-la num array de dependência) sem recriar a cadeia a cada render.
export function useInvalidateCallForms() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: callFormKeys.list });
    void queryClient.invalidateQueries({ queryKey: callFormKeys.active });
  }, [queryClient]);
}

// Ativar/desativar. O ramo mora aqui porque é exatamente o que a tabela fazia
// inline, e as duas pontas invalidam igual. Os `mutationFn` daqui chamam a
// função de api dentro de uma seta: o TanStack invoca o mutationFn com
// (variáveis, contexto), e uma função de api que aceite um segundo parâmetro
// receberia o contexto como se fosse dado.
export function useToggleCallForm(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateCallForms();
  return useMutation({
    mutationFn: (form: CallFormDef) =>
      form.active ? deactivateCallForm(form.id) : activateCallForm(form.id),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

export function useDuplicateCallForm(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateCallForms();
  return useMutation({
    mutationFn: (id: string) => duplicateCallForm(id),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

export function useDeleteCallForm(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateCallForms();
  return useMutation({
    mutationFn: (id: string) => deleteCallForm(id),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

// Salvar o construtor: cria ou atualiza conforme haja id, e ATIVA em seguida se
// a caixa estiver marcada e o formulário salvo ainda não estiver ativo. Os três
// passos são uma mutação só porque para quem clica "Salvar" é uma ação só — e um
// erro no activate precisa cair no mesmo onError do save.
export function useSaveCallForm(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateCallForms();
  return useMutation({
    mutationFn: async (variables: {
      id: string | null;
      input: { name: string; description?: string | null; fields: FormField[] };
      activate: boolean;
    }) => {
      const saved = variables.id
        ? await updateCallForm(variables.id, variables.input)
        : await createCallForm(variables.input);
      if (variables.activate && !saved.active) await activateCallForm(saved.id);
      return saved;
    },
    // Invalida ANTES do handler, ao contrário dos hooks acima: o `onSuccess` do
    // construtor fecha o assistente (`onBack`), e a ordem aqui é a que a tela
    // sempre teve — invalidar as duas chaves e só então sair.
    onSuccess: () => {
      invalidate();
      handlers.onSuccess?.();
    },
    onError: handlers.onError,
  });
}

// Enviar as respostas de uma chamada. Sem invalidação: nada no front lê o que
// foi gravado — quem precisa saber é o relatório, que busca de novo quando abre.
export function useSubmitCallForm(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  return useMutation({
    mutationFn: (variables: { callId: string; answers: Record<string, unknown> }) =>
      submitCallForm(variables.callId, variables.answers),
    onSuccess: () => handlers.onSuccess?.(),
    onError: handlers.onError,
  });
}
